import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText, LayoutDashboard, Lightbulb, ListChecks } from 'lucide-react';
import { CUSTOM_DASHBOARD_TEMPLATE, DASHBOARD_TEMPLATES } from '../helpers/dashboardTemplates';
import GuideAccountCTA from '../components/GuideAccountCTA';
import './ApplicationTimelinePage.css';

const templateDetails = {
  searching: {
    why: 'This is the best starting point when your list is still broad. It helps you compare fit, cost, location, scores, acceptance rate, and basic school data in one place.',
    use: 'Use it before Progress. Add possible colleges here, compare them, then move only the real application schools into Progress Final College List.',
  },
  costs: {
    why: 'Cost can change whether a school is realistic. This dashboard keeps tuition, net price, grants, loans, and debt data visible before a school becomes a final choice.',
    use: 'Use it when a college looks interesting but you still need to check affordability, scholarships, and family budget fit.',
  },
  tours: {
    why: 'Campus visits and virtual tours blur together fast. This keeps tour dates, campus feel, pros, cons, questions, and admissions contacts from getting lost.',
    use: 'Use it while narrowing your list so your decision is based on actual fit, not just rankings or one good photo.',
  },
  applications: {
    why: 'This is for the final submission process: deadlines, application links, portal details, notes, and whether SAT or ACT scores need to be sent.',
    use: 'Use it once you are actively applying. It pairs well with Progress, which keeps the final college list, deadlines, tasks, and submitted status focused.',
  },
  scholarships: {
    why: 'Scholarship deadlines can be separate from application deadlines. This keeps names, amounts, links, requirements, and submission status organized.',
    use: 'Use it for school scholarships, local scholarships, outside scholarships, honors college awards, or any separate money application.',
  },
  essays: {
    why: 'Essays need their own space because prompts, word limits, draft links, feedback, final review, and submission status can multiply quickly.',
    use: 'Use it like a bookmarked essay tab. Save each prompt and essay link so your drafts are one click away instead of scattered across tabs and documents.',
  },
  contacts: {
    why: 'Admissions emails and questions are easy to forget. This keeps track of who you contacted, when, and what you asked.',
    use: 'Use it for admissions counselors, financial aid offices, department contacts, interview follow-ups, and questions from tours or info sessions.',
  },
  decisions: {
    why: 'After decisions arrive, the work changes. You need to compare admits, waitlists, financial aid, deposits, deadlines, and enrollment intent.',
    use: 'Use it from decision season through commitment so acceptances, aid offers, and deposit deadlines are not mixed into research notes.',
  },
  'css-fafsa': {
    why: 'Financial aid has its own codes, forms, deadlines, and confirmation steps. FAFSA and CSS Profile requirements vary by school.',
    use: 'Use it to track FAFSA codes, CSS codes, priority deadlines, required forms, submitted status, and whether each school received your materials.',
  },
  custom: {
    why: 'Sometimes your process needs a table that does not fit a preset. Start from Scratch gives you a clean dashboard with only the fields you choose.',
    use: 'Use it for personal workflows, special programs, auditions, portfolios, athletics, summer programs, or anything that needs custom tracking.',
  },
};

const topTips = [
  'Use dashboards for research and organization before everything becomes final.',
  'Use Progress Final College List only for schools you are actively applying to.',
  'Start with Searching Process and College Tours when you are narrowing your list.',
  'Use Essay Tracker as a home base for prompts, word limits, draft links, and revision status.',
  'Use Application Tracker and CSS & FAFSA when deadlines and submissions start getting real.',
  'Templates are starting points. You can still add, hide, resize, and customize columns.',
];

const sources = [
  { label: 'Common App first-year guide', url: 'https://www.commonapp.org/apply/first-year-students/' },
  { label: 'BigFuture college search', url: 'https://bigfuture.collegeboard.org/college-search' },
  { label: 'FAFSA', url: 'https://studentaid.gov/h/apply-for-aid/fafsa' },
  { label: 'CSS Profile', url: 'https://cssprofile.collegeboard.org/' },
];

function DashboardTemplateGuidePage() {
  const templates = [...DASHBOARD_TEMPLATES, CUSTOM_DASHBOARD_TEMPLATE];

  return (
    <main className="timeline-page">
      <div className="timeline-page__inner">
        <header className="timeline-page__hero">
          <span className="timeline-page__eyebrow">Dashboard Template Guide</span>
          <h1>What each dashboard is for</h1>
          <p>
            Dashboard templates are meant to reduce blank-page stress. Pick the one that matches the stage
            you are in: researching schools, narrowing your list, tracking essays, submitting applications,
            handling financial aid, or comparing decisions.
          </p>
        </header>

        <section className="timeline-page__intro" aria-label="Dashboard reminder">
          <div className="timeline-page__intro-icon">
            <LayoutDashboard size={19} strokeWidth={1.8} />
          </div>
          <div>
            <h2>Dashboards first, Progress when final</h2>
            <p>
              Use dashboards while you are researching and comparing. When a college becomes a real
              application, add it to Progress under Final College List so deadlines, reminders, Calendar,
              and Individual Progress all use the same focused list.
            </p>
          </div>
        </section>

        <section className="timeline-page__note timeline-page__note--action" aria-label="Open MaiColleges tools">
          <span className="timeline-page__action-kicker">Use MaiColleges</span>
          <h2>Pick a dashboard based on your stage</h2>
          <p>
            If you are still deciding, open Dashboards and choose a template for the work in front of you.
            If your list is final, open Progress and add those schools under Final College List.
          </p>
          <div className="timeline-page__action-row">
            <Link to="/dashboard" className="timeline-page__internal-link">Open Dashboards</Link>
            <Link to="/progress" className="timeline-page__internal-link timeline-page__internal-link--secondary">Open Final College List</Link>
          </div>
        </section>

        <section className="timeline-page__tips" aria-labelledby="dashboard-template-tips-title">
          <div className="timeline-page__section-heading">
            <Lightbulb size={18} strokeWidth={1.8} />
            <h2 id="dashboard-template-tips-title">Top tips</h2>
          </div>
          <div className="timeline-page__tip-grid">
            {topTips.map((tip) => (
              <article className="timeline-tip" key={tip}>
                <ListChecks size={16} strokeWidth={1.9} />
                <p>{tip}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__timeline" aria-labelledby="dashboard-template-list-title">
          <div className="timeline-page__section-heading">
            <FileText size={18} strokeWidth={1.8} />
            <h2 id="dashboard-template-list-title">Template guide</h2>
          </div>
          <div className="timeline-list">
            {templates.map((template) => {
              const detail = templateDetails[template.id];
              return (
                <article className="timeline-block" key={template.id}>
                  <div className="timeline-block__marker" aria-hidden="true" />
                  <div className="timeline-block__content">
                    <h3>{template.name}</h3>
                    <p>{template.description}</p>
                    <ul>
                      <li><strong>Why it matters:</strong> {detail?.why}</li>
                      <li><strong>When to use it:</strong> {detail?.use}</li>
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="timeline-page__sources" aria-labelledby="dashboard-template-sources-title">
          <h2 id="dashboard-template-sources-title">Helpful official sources</h2>
          <div className="timeline-source-grid">
            {sources.map((source) => (
              <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>
                <span>{source.label}</span>
                <ExternalLink size={14} strokeWidth={1.9} />
              </a>
            ))}
          </div>
        </section>

        <GuideAccountCTA title="Choose a template and build your first dashboard" />
      </div>
    </main>
  );
}

export default DashboardTemplateGuidePage;
